import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  CalendarIcon, 
  UserIcon, 
  TagIcon, 
  ArrowLeftIcon,
  ShareIcon,
  BookmarkIcon
} from '@heroicons/react/24/outline';
import SEOHead from '../../components/SEO/SEOHead';
import defaultSEO from '../../components/SEO/seoConfig';

// Sample blog posts data (same as in BlogPage.jsx)
const sampleBlogPosts = [
  {
    id: 1,
    title: 'Top 10 Wildlife Photography Tips',
    slug: 'wildlife-photography-tips',
    excerpt: 'Discover the most breathtaking wildlife encounters that Kenya has to offer, from the Great Migration to rare sightings of the Big Five.',
    content: `
      <p>Kenya is renowned worldwide for its incredible wildlife experiences, offering some of the most spectacular safari opportunities on the planet. From the iconic Great Migration to intimate encounters with the Big Five, Kenya's diverse ecosystems provide unparalleled wildlife viewing.</p>
      
      <h2>1. Witness the Great Migration in Maasai Mara</h2>
      <p>The annual wildebeest migration is often described as one of the world's most spectacular natural events. Between July and October, over 1.5 million wildebeest, along with hundreds of thousands of zebras and gazelles, cross from Tanzania's Serengeti into Kenya's Maasai Mara in search of fresh grazing. The dramatic river crossings, where crocodiles lie in wait, create heart-stopping moments that epitomize the raw drama of nature.</p>
      
      <h2>2. Track the Big Five in Amboseli National Park</h2>
      <p>With the iconic backdrop of Mount Kilimanjaro, Amboseli offers some of the best opportunities to see large elephant herds up close. The park's relatively open landscape makes it easier to spot lions, leopards, buffalo, and rhinos, completing your Big Five checklist against one of Africa's most dramatic backdrops.</p>
      
      <h2>3. Bird Watching at Lake Nakuru</h2>
      <p>Famous for its flocks of flamingos that turn the lake's shores pink, Lake Nakuru National Park is a bird lover's paradise with over 450 bird species. Beyond the flamingos, the park is home to both black and white rhinos, Rothschild's giraffes, and numerous predators.</p>
      
      <h2>4. Night Game Drives in Samburu</h2>
      <p>Samburu National Reserve offers the rare opportunity for night game drives, revealing a whole different world of nocturnal wildlife. Spot elusive creatures like aardvarks, genets, and prowling big cats as they begin their nighttime hunts.</p>
      
      <h2>5. Walking Safaris in Laikipia</h2>
      <p>Experience wildlife on foot with expert guides in the private conservancies of Laikipia. Walking safaris provide an intimate connection with the environment, allowing you to notice smaller details and track animals through the landscape.</p>
      
      <h2>6. Marine Wildlife in Watamu Marine National Park</h2>
      <p>Kenya's wildlife experiences extend beyond the savannah. Watamu offers incredible opportunities to snorkel or dive among vibrant coral reefs teeming with tropical fish. Between November and April, you can also witness whale sharks and humpback whales on their migratory routes.</p>
      
      <h2>7. Rhino Tracking in Ol Pejeta Conservancy</h2>
      <p>Home to the last two northern white rhinos on the planet, Ol Pejeta Conservancy offers unparalleled rhino experiences. Join rangers on rhino tracking expeditions and learn about critical conservation efforts to save these magnificent creatures from extinction.</p>
      
      <h2>8. Hot Air Balloon Safari over Maasai Mara</h2>
      <p>Gain a bird's-eye view of Kenya's wildlife with a dawn hot air balloon ride over the Maasai Mara. Floating silently above the plains as the sun rises, you'll witness animals in their natural habitat from a unique perspective.</p>
      
      <h2>9. Elephant Encounters in Tsavo</h2>
      <p>Tsavo East and West National Parks are home to some of Kenya's largest elephant herds, often covered in the distinctive red dust of the region. These "red elephants" create unforgettable photo opportunities against the park's arid landscape.</p>
      
      <h2>10. Primate Watching in Kakamega Forest</h2>
      <p>For a completely different wildlife experience, visit Kakamega Forest, Kenya's last remnant of the ancient Guineo-Congolian rainforest. Here you can spot rare primates like the blue monkey and black-and-white colobus, along with hundreds of butterfly species and forest birds found nowhere else in Kenya.</p>
      
      <p>When planning your Kenya wildlife adventure, consider visiting during the dry seasons (January-February and June-September) when animals concentrate around water sources, making them easier to spot. Working with experienced guides who understand animal behavior will significantly enhance your wildlife encounters, ensuring you don't just see animals but truly understand and appreciate them in their natural habitat.</p>
    `,
    image: 'https://images.unsplash.com/photo-1523805009345-7448845a9e53?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2072&q=80',
    author: {
      name: 'Timothy Kimani',
      avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
      bio: 'Wildlife photographer and safari guide with over 10 years of experience in Kenya\'s national parks.'
    },
    date: '2024-12-15',
    category: 'Wildlife',
    tags: ['safari', 'wildlife', 'big five', 'kenya']
  },
  {
    id: 2,
    title: 'Essential Packing Guide for Safari Adventures',
    slug: 'safari-packing-guide',
    excerpt: 'Experience the rich traditions and vibrant culture of the Maasai people through immersive village visits and authentic cultural exchanges.',
    content: `
      <p>The Maasai people are one of Kenya's most iconic and culturally rich tribes, known for their distinctive dress, intricate beadwork, and semi-nomadic lifestyle. A cultural journey through Maasai villages offers travelers a unique opportunity to learn about traditional ways of life that have remained largely unchanged for centuries, despite the pressures of modernization.</p>
      
      <h2>The Maasai People: Guardians of Tradition</h2>
      <p>The Maasai are pastoralists who have traditionally lived in Kenya and Tanzania, herding cattle which they consider sacred. Their society is organized around age-sets, with young men passing through various stages including the famous warrior (moran) phase. Women play crucial roles in building homes, raising children, and creating the beautiful beadwork for which the tribe is renowned.</p>
      
      <p>Today, many Maasai communities are finding ways to preserve their cultural heritage while adapting to changing times. Cultural tourism has become an important source of income, allowing them to maintain their traditions while gaining economic benefits.</p>
      
      <h2>Authentic Village Visits</h2>
      <p>When visiting a Maasai village, you'll typically be greeted with traditional songs and the famous jumping dance (adumu) performed by warriors. This welcome ceremony is not merely a show for tourists but a genuine expression of Maasai hospitality and cultural pride.</p>
      
      <p>Inside the village, you'll have the opportunity to see traditional manyatta homes made from mud, sticks, grass, and cow dung. These ingenious structures are perfectly adapted to the local environment, providing protection from both heat and rain.</p>
      
      <h2>Cultural Exchange and Learning</h2>
      <p>The most meaningful village visits involve genuine cultural exchange. You might learn how the Maasai make fire using traditional methods, discover the medicinal plants they've used for generations, or try your hand at beadwork alongside Maasai women.</p>
      
      <p>Many villages offer demonstrations of traditional skills such as spear throwing, fire making, or cattle herding. These activities provide insights into the practical knowledge that has allowed the Maasai to thrive in sometimes harsh environments.</p>
      
      <h2>Responsible Cultural Tourism</h2>
      <p>When planning a visit to Maasai villages, it's important to choose operators who practice responsible tourism. The best experiences benefit the community directly, with a significant portion of fees going to support local projects such as schools, water systems, or healthcare facilities.</p>
      
      <p>Respectful photography is essential - always ask permission before taking photos, and be willing to pay a small fee if requested. Remember that you're visiting people's homes, not a museum exhibit.</p>
      
      <h2>Beyond the Tourist Experience</h2>
      <p>For travelers seeking deeper cultural immersion, some communities offer overnight stays or multi-day experiences. These extended visits allow you to participate in daily activities, share meals with families, and gain a more nuanced understanding of Maasai life.</p>
      
      <p>Learning a few words in Maa (the Maasai language) can greatly enhance your experience. Simple greetings like "Sopa" (hello) demonstrate respect and interest in the culture.</p>
      
      <h2>Conservation and Culture</h2>
      <p>Many Maasai communities are now involved in conservation efforts, recognizing that protecting wildlife and natural resources is essential for their future. Community conservancies allow the Maasai to benefit from wildlife tourism while maintaining their cultural connection to the land.</p>
      
      <p>By visiting these conservancies, travelers support a model that integrates cultural preservation with environmental conservation - a sustainable approach that benefits both the Maasai people and Kenya's precious ecosystems.</p>
      
      <p>A cultural journey through Maasai villages offers far more than colorful photographs. It provides an opportunity to connect with one of Africa's most resilient cultures, to learn from their deep wisdom about living in harmony with nature, and to support communities as they navigate the complex path between tradition and modernity.</p>
    `,
    image: 'https://images.unsplash.com/photo-1489493585363-d69421e0edd3?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80',
    author: {
      name: 'Sarah Njeri',
      avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
      bio: 'Cultural anthropologist specializing in East African tribal traditions and sustainable tourism practices.'
    },
    date: '2024-11-28',
    category: 'Culture',
    tags: ['maasai', 'culture', 'traditions', 'community']
  },
  {
    id: 3,
    title: 'Best Season to Visit East African National Parks',
    slug: 'best-season-east-africa',
    excerpt: 'Plan your Mount Kenya adventure with our comprehensive seasonal guide, covering weather patterns, climbing conditions, and wildlife sightings.',
    content: 'Full article content here...',
    image: 'https://images.unsplash.com/photo-1573979267151-5f4f23ae7873?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2071&q=80',
    author: {
      name: 'John Kamau',
      avatar: 'https://randomuser.me/api/portraits/men/22.jpg',
      bio: 'Professional mountain guide with over 200 successful Mount Kenya expeditions.'
    },
    date: '2024-11-10',
    category: 'Adventure',
    tags: ['mount kenya', 'hiking', 'climbing', 'seasons']
  },
  {
    id: 4,
    title: 'Sustainable Tourism Practices in Kenya',
    slug: 'sustainable-tourism-practices-kenya',
    excerpt: 'Learn how Kenya is leading the way in sustainable tourism with eco-friendly lodges, community-based tourism initiatives, and conservation efforts.',
    content: 'Full article content here...',
    image: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80',
    author: {
      name: 'Elizabeth Wanjiku',
      avatar: 'https://randomuser.me/api/portraits/women/28.jpg',
      bio: 'Environmental consultant working with tourism businesses to implement sustainable practices.'
    },
    date: '2024-10-25',
    category: 'Conservation',
    tags: ['sustainability', 'eco-tourism', 'conservation', 'community']
  },
  {
    id: 5,
    title: 'Kenya\'s Hidden Beach Paradises',
    slug: 'kenyas-hidden-beach-paradises',
    excerpt: 'Explore Kenya\'s lesser-known coastal gems, from secluded beaches and pristine marine reserves to charming coastal villages.',
    content: 'Full article content here...',
    image: 'https://images.unsplash.com/photo-1505881502353-a1986add3762?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2072&q=80',
    author: {
      name: 'Timothy Kimani',
      avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
      bio: 'Wildlife photographer and safari guide with over 10 years of experience in Kenya\'s national parks.'
    },
    date: '2024-10-12',
    category: 'Beaches',
    tags: ['beaches', 'coast', 'ocean', 'relaxation']
  },
  {
    id: 6,
    title: 'Safari Photography Tips for Beginners',
    slug: 'safari-photography-tips-beginners',
    excerpt: 'Capture stunning wildlife photos on your Kenya safari with these essential photography tips, equipment recommendations, and composition techniques.',
    content: 'Full article content here...',
    image: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80',
    author: {
      name: 'John Kamau',
      avatar: 'https://randomuser.me/api/portraits/men/22.jpg',
      bio: 'Professional mountain guide with over 200 successful Mount Kenya expeditions.'
    },
    date: '2024-09-30',
    category: 'Photography',
    tags: ['photography', 'safari', 'wildlife', 'tips']
  },
  {
    id: 7,
    title: 'Family-Friendly Safari Experiences in Kenya',
    slug: 'family-friendly-safari-experiences-kenya',
    excerpt: 'Plan the perfect family safari with our guide to kid-friendly lodges, age-appropriate activities, and educational wildlife experiences in Kenya.',
    content: 'Full article content here...',
    image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2071&q=80',
    author: {
      name: 'Sarah Njeri',
      avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
      bio: 'Cultural anthropologist specializing in East African tribal traditions and sustainable tourism practices.'
    },
    date: '2024-09-15',
    category: 'Family',
    tags: ['family', 'kids', 'safari', 'activities']
  },
  {
    id: 8,
    title: 'Luxury Safari Lodges in Kenya',
    slug: 'luxury-safari-lodges-kenya',
    excerpt: 'Experience the height of safari luxury with our curated selection of Kenya\'s most exclusive lodges, offering unparalleled comfort and wildlife viewing.',
    content: 'Full article content here...',
    image: 'https://images.unsplash.com/photo-1493246507139-91e8fad9978e?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80',
    author: {
      name: 'Elizabeth Wanjiku',
      avatar: 'https://randomuser.me/api/portraits/women/28.jpg',
      bio: 'Environmental consultant working with tourism businesses to implement sustainable practices.'
    },
    date: '2024-09-05',
    category: 'Luxury',
    tags: ['luxury', 'lodges', 'accommodation', 'exclusive']
  }
];

// Function to get related posts
const getRelatedPosts = (currentPost, allPosts, count = 3) => {
  return allPosts
    .filter(post => 
      post.id !== currentPost.id && 
      (post.category === currentPost.category || 
       post.tags.some(tag => currentPost.tags.includes(tag)))
    )
    .sort(() => 0.5 - Math.random()) // Simple random sort
    .slice(0, count);
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    // Simulate API call to fetch post by slug
    setLoading(true);
    
    try {
      // Find post with matching slug
      const foundPost = sampleBlogPosts.find(p => p.slug === slug);
      
      if (foundPost) {
        setPost(foundPost);
        
        // Get related posts
        const related = getRelatedPosts(foundPost, sampleBlogPosts);
        setRelatedPosts(related);
      } else {
        setError('Blog post not found');
      }
    } catch (err) {
      setError('Error loading blog post');
      console.error(err);
    } finally {
      setLoading(false);
    }
    
    // Scroll to top when post changes
    window.scrollTo(0, 0);
  }, [slug]);
  
  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  if (loading) {
    return (
      <div className="container-custom mx-auto py-16 text-center">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/2 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4 mx-auto mb-12"></div>
          <div className="h-96 bg-gray-200 rounded mb-8"></div>
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container-custom mx-auto py-16 text-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-red-700 mb-4">Error</h2>
          <p className="text-red-600 mb-6">{error}</p>
          <Link 
            to="/blog" 
            className="bg-primary hover:bg-primary-dark text-white font-medium px-6 py-3 rounded-md transition-colors inline-block"
          >
            Return to Blog
          </Link>
        </div>
      </div>
    );
  }
  
  if (!post) {
    return null;
  }
  
  // Generate SEO metadata for this blog post
  const generateBlogPostSEO = () => {
    if (!post) return null;
    
    // Create a clean description without HTML tags
    const stripHtml = (html) => {
      if (!html) return '';
      return html.replace(/<[^>]*>/g, '');
    };
    
    // Get the first paragraph of content for the description
    const contentText = stripHtml(post.content);
    const firstParagraph = contentText.split('\n')[0];
    const shortDescription = firstParagraph.length > 160 ? 
      `${firstParagraph.substring(0, 157)}...` : firstParagraph;
    
    // Use excerpt as fallback if first paragraph extraction fails
    const description = shortDescription || post.excerpt;
    
    // Generate keywords from post data
    const keywords = [
      post.title,
      post.category,
      ...(Array.isArray(post.tags) ? post.tags : []),
      'blog',
      'article',
      post.author.name
    ].filter(Boolean);
    
    // Generate structured data for this blog post
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      image: post.image,
      datePublished: new Date(post.date).toISOString(),
      dateModified: new Date(post.date).toISOString(),
      author: {
        '@type': 'Person',
        name: post.author.name
      },
      publisher: {
        '@type': 'Organization',
        name: defaultSEO.siteName,
        logo: {
          '@type': 'ImageObject',
          url: `${defaultSEO.siteUrl}/images/logo.png`
        }
      },
      description: description,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${defaultSEO.siteUrl}/blog/${post.slug}`
      },
      keywords: keywords
    };
    
    return {
      title: post.title,
      description: description,
      keywords: keywords,
      canonicalUrl: `${defaultSEO.siteUrl}/blog/${post.slug}`,
      ogType: 'article',
      ogImage: post.image,
      structuredData: structuredData
    };
  };
  
  // Get SEO data
  const blogSEO = generateBlogPostSEO();

  return (
    <div className="bg-gray-50">
      {/* SEO Head */}
      {blogSEO && (
        <SEOHead
          title={blogSEO.title}
          description={blogSEO.description}
          keywords={blogSEO.keywords}
          canonicalUrl={blogSEO.canonicalUrl}
          ogType={blogSEO.ogType}
          ogImage={blogSEO.ogImage}
          structuredData={blogSEO.structuredData}
        />
      )}
      {/* Hero Section */}
      <div className="relative bg-gray-900 text-white">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${post.image})` }}
        ></div>
        <div className="container-custom mx-auto py-20 relative z-10">
          <Link 
            to="/blog" 
            className="inline-flex items-center text-gray-200 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Blog
          </Link>
          
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-5xl font-bold mb-6">{post.title}</h1>
            
            <div className="flex flex-wrap items-center text-gray-200 mb-4">
              <span className="bg-primary-light text-primary text-sm font-medium px-3 py-1 rounded-full mr-4 mb-2">
                {post.category}
              </span>
              
              <div className="flex items-center mr-4 mb-2">
                <CalendarIcon className="h-4 w-4 mr-1" />
                <span>{formatDate(post.date)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Article Content */}
      <div className="container-custom mx-auto py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <article className="bg-white rounded-lg shadow-md p-6 md:p-10">
              {/* Author Info */}
              <div className="flex items-center mb-8 pb-8 border-b border-gray-100">
                <img 
                  src={post.author.avatar} 
                  alt={post.author.name} 
                  className="w-12 h-12 rounded-full mr-4 object-cover"
                />
                <div>
                  <h3 className="font-bold text-gray-800">{post.author.name}</h3>
                  <p className="text-sm text-gray-600">{post.author.bio}</p>
                </div>
              </div>
              
              {/* Article Content */}
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: post.content }}
              ></div>
              
              {/* Tags */}
              <div className="mt-10 pt-6 border-t border-gray-100">
                <h4 className="text-sm font-bold text-gray-700 mb-3">Tags:</h4>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map(tag => (
                    <Link 
                      key={tag}
                      to={`/blog?search=${tag}`}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm px-3 py-1 rounded-full transition-colors"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>
              
              {/* Share Buttons */}
              <div className="mt-8 pt-6 border-t border-gray-100">
                <h4 className="text-sm font-bold text-gray-700 mb-3">Share this article:</h4>
                <div className="flex space-x-2">
                  <button className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full transition-colors">
                    <ShareIcon className="h-5 w-5" />
                  </button>
                  <button className="bg-green-600 hover:bg-green-700 text-white p-2 rounded-full transition-colors">
                    <ShareIcon className="h-5 w-5" />
                  </button>
                  <button className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full transition-colors">
                    <ShareIcon className="h-5 w-5" />
                  </button>
                  <button className="bg-blue-400 hover:bg-blue-500 text-white p-2 rounded-full transition-colors">
                    <ShareIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </article>
          </div>
          
          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Author Card */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h3 className="text-xl font-bold mb-4 text-gray-800">About the Author</h3>
              <div className="flex items-center mb-4">
                <img 
                  src={post.author.avatar} 
                  alt={post.author.name} 
                  className="w-16 h-16 rounded-full mr-4 object-cover"
                />
                <h4 className="font-bold text-gray-800">{post.author.name}</h4>
              </div>
              <p className="text-gray-600 mb-4">{post.author.bio}</p>
              <Link 
                to={`/blog?search=${post.author.name}`}
                className="text-primary hover:text-primary-dark font-medium transition-colors"
              >
                View all posts by this author
              </Link>
            </div>
            
            {/* Related Posts */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold mb-4 text-gray-800">Related Articles</h3>
              
              {relatedPosts.length > 0 ? (
                <div className="space-y-6">
                  {relatedPosts.map(relatedPost => (
                    <div key={relatedPost.id} className="flex">
                      <img 
                        src={relatedPost.image} 
                        alt={relatedPost.title} 
                        className="w-20 h-20 object-cover rounded-md mr-4"
                      />
                      <div>
                        <Link 
                          to={`/blog/${relatedPost.slug}`}
                          className="font-medium text-gray-800 hover:text-primary transition-colors line-clamp-2"
                        >
                          {relatedPost.title}
                        </Link>
                        <p className="text-sm text-gray-500 mt-1">
                          {formatDate(relatedPost.date)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">No related articles found.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPostPage;
